import type { PayloadAction } from '@reduxjs/toolkit';
import { createSelector, createSlice } from '@reduxjs/toolkit';
import type {
  PayGroupCategories,
  PayHubCashierContractsCashierSettingsResponse,
  PayHubCashierContractsPaymentsSelectPaymentGroupResponse,
  PayHubCashierContractsTaxResponse,
} from 'store/models';
import { extendedDepositAPI } from 'store/services/depositAPI';
import { getCachedGroupAttributes } from 'utils/common';

import { DEFAULT_EMPTY_CURRENCY_ALPHA3, DEFAULT_EMPTY_CURRENCY_PRECISION } from '../constants';
import type { UIPayment, UIPaymentGroups } from '../formatters';

interface DepositSlice extends UIPayment {
  currentMethod?: UIPaymentGroups;
  isAutoSubmitted?: boolean;
  skipAutoSelectMethod?: boolean;
  responceByReqId?: Record<string, PayHubCashierContractsPaymentsSelectPaymentGroupResponse | null>;
  requestGroupIds?: Record<string, string | null>;
  uploadedFileFormData?: File | null;
  isDisplayPage?: boolean;
  paygroupCategories?: PayGroupCategories;
  depositMethods?: UIPaymentGroups[];
}

const initialState: DepositSlice = {
  methodsTypes: [],
  isReady: false,
  canAutoRedirect: false,
  isRequestPending: false,
  submitPaymentReady: false,
  currencyId: undefined,
  currencyAlpha3: undefined,
  methodToRedirect: undefined,
  widgetSettings: {},
  methods: [],
  carouselMethodList: [],
  growthbookFeatures: [],
  viewData: [],
  tax: {},
  userBalance: 0,
  isAutoSubmitted: false,
  skipAutoSelectMethod: false,
  responceByReqId: {},
  requestGroupIds: {},
  userBonuses: null,
  uploadedFileFormData: null,
  authToken: '',
  merchantApiKey: '',
  isDisplayPage: false,
  paygroupCategories: undefined,
  depositMethods: [],
  walletCurrencies: [],
  shouldSkipInitFeature: false,
  selectedCurrency: null,
  userCurrency: null,
  userBlocked: false,
  defaultFiatCurrency: null,
  meshMethods: [],
  pendingWithdrawals: [],
};

export const depositSlice = createSlice({
  name: 'deposit',
  initialState,
  reducers: {
    setWidgetSettings(state, action: PayloadAction<PayHubCashierContractsCashierSettingsResponse>) {
      return {
        ...state,
        widgetSettings: action.payload,
      };
    },
    setCurrentMethod: (state, action: PayloadAction<UIPaymentGroups>) => ({
      ...state,
      currentMethod: action.payload,
    }),
    setDepositTax: (state, action: PayloadAction<PayHubCashierContractsTaxResponse | undefined>) => ({
      ...state,
      tax: action.payload,
    }),
    setIsAutoSubmitted: (state, { payload }: PayloadAction<boolean>) => ({
      ...state,
      isAutoSubmitted: payload,
    }),
    cleanCurrentMethod(state) {
      return {
        ...state,
        isAutoSubmitted: false,
        currentMethod: undefined,
      };
    },
    setSkippingAutoSelectMethod(state) {
      return {
        ...state,
        skipAutoSelectMethod: true,
      };
    },
    resetSkippingAutoSelectMethod(state) {
      return {
        ...state,
        skipAutoSelectMethod: false,
      };
    },
    setUploadedFile: (state, action: PayloadAction<File | null>) => ({
      ...state,
      uploadedFileFormData: action.payload,
    }),
    setIsDisplayPage: (state, action: PayloadAction<boolean>) => ({
      ...state,
      isDisplayPage: action.payload,
    }),
    setPaygroupCategories: (state, action: PayloadAction<PayGroupCategories | undefined>) => {
      state.paygroupCategories = action.payload;
    },
    setDepositMethods: (state, action: PayloadAction<UIPaymentGroups[]>) => {
      state.depositMethods = action.payload;
    },
    setShouldSkipInitFeature: (state, action: PayloadAction<boolean>) => {
      state.shouldSkipInitFeature = action.payload;
    },
    setAmountStepConfig: (
      state,
      action: PayloadAction<{
        amountCanBeChanged: boolean;
        possibleChangesAmount: number;
      }>,
    ) => {
      if (state.currentMethod) {
        state.currentMethod.amountCanBeChanged = action.payload.amountCanBeChanged;
        state.currentMethod.possibleChangesAmount = action.payload.possibleChangesAmount;
      }
    },
  },

  extraReducers: (builder) => {
    builder.addMatcher(extendedDepositAPI.endpoints.getDepositMethodInfo.matchPending, (state, action) => {
      const { payGroupId } = action.meta.arg.originalArgs;
      if (state.requestGroupIds) {
        state.requestGroupIds[payGroupId.toString()] = action.meta.requestId;
      }
    });
    builder.addMatcher(extendedDepositAPI.endpoints.getDepositMethodInfo.matchFulfilled, (state, action) => {
      const { payGroupId } = action.meta.arg.originalArgs;
      const requestId = state.requestGroupIds ? state.requestGroupIds[payGroupId] : null;
      if (requestId === action.meta.requestId && state.responceByReqId) {
        state.responceByReqId[action.meta.requestId] = action.payload;
      }
      const methodInfo = action.payload as unknown as { amountCanBeChanged?: boolean; possibleChangesAmount?: number };
      if (state.currentMethod) {
        state.currentMethod.amountCanBeChanged = methodInfo?.amountCanBeChanged || false;
        state.currentMethod.possibleChangesAmount = methodInfo?.possibleChangesAmount || 0;
      }
    });
  },
});

export const depositSelector = (state: { deposit?: DepositSlice }) => state.deposit;

export const depositTaxSelector = createSelector(depositSelector, (deposit) => (deposit ? deposit.tax || {} : {}));

export const currentDepositMethodSelector = (state: { deposit?: DepositSlice }) => state.deposit?.currentMethod;

export const methodNameSelector = (state: { deposit?: DepositSlice }) =>
  state.deposit?.currentMethod?.displayName || '';

export const methodShortNameSelector = (state: { deposit?: DepositSlice }) => state.deposit?.currentMethod?.name || '';

export const isSelfTargetSelector = (state: { deposit?: DepositSlice }) =>
  Boolean(state?.deposit?.currentMethod?.isSelfTarget);

export const isAutoSubmittedSelector = ({ deposit }: { deposit: DepositSlice }) => deposit.isAutoSubmitted;

export const skipAutoSelectMethodSelector = ({ deposit }: { deposit: DepositSlice }) =>
  Boolean(deposit.skipAutoSelectMethod);

export const currentDepositMethodCurrencySelector = (state: { deposit?: DepositSlice }) =>
  state?.deposit?.currentMethod?.currencyAlpha3 ||
  getCachedGroupAttributes().currencyAttr ||
  DEFAULT_EMPTY_CURRENCY_ALPHA3;

export const currentDepositMethodCurrencyPrecisionSelector = (state: { deposit?: DepositSlice }) =>
  state?.deposit?.currentMethod?.currencyPrecision ?? DEFAULT_EMPTY_CURRENCY_PRECISION;

export const isAutoSubmittedInitSelector = (state: { deposit?: DepositSlice }) =>
  state.deposit?.currentMethod?.autoSubmit || false;

export const requestIdSelector = (deposit: DepositSlice, payGroupId: number) => deposit?.requestGroupIds?.[payGroupId];

export const methodInfoSelector = (
  {
    deposit,
  }: {
    deposit: DepositSlice;
  },
  payGroupId: number,
) => {
  const requestId = requestIdSelector(deposit, payGroupId);
  return typeof requestId === 'string' && deposit.responceByReqId ? deposit.responceByReqId[requestId] : null;
};

export const currencyDisplaySelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings.currency_display_setting_id;

export const uploadedFileFormDataSelector = (state: { deposit?: DepositSlice }) =>
  state.deposit?.uploadedFileFormData || null;

export const isDisplayPageSelector = (state: { deposit?: DepositSlice }) => state.deposit?.isDisplayPage || false;

export const isBonusInAmountBlockSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.bonus_in_amount_block || false;

export const isBonusInAmountFieldSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.bonus_in_amount_field || false;

export const shortAmountBlockSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.short_amount_block || false;

export const sumAmountBlockSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.sum_amount_block || false;

export const kycCardVerificationSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.kyc_card_verification || false;

export const showBonusInfolineSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.show_bonus_info_line || false;

export const overrideBonusDepositAmountSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.override_bonus_deposit_amount || false;

export const showBonusMinDepositAmountSelector = (state: { deposit: DepositSlice }) =>
  state.deposit.widgetSettings?.show_bonus_min_deposit_amount || false;

export const amountCanBeChangedSelector = (state: { deposit?: DepositSlice }) =>
  state.deposit?.currentMethod?.amountCanBeChanged || false;

export const possibleChangesAmountSelector = (state: { deposit?: DepositSlice }): number =>
  state.deposit?.currentMethod?.possibleChangesAmount || 0;

export const paygroupCategoriesSelector = (state: { deposit?: DepositSlice }) => state.deposit?.paygroupCategories;

export const showAllMethodsDepositSelector = (state: { deposit?: DepositSlice }) =>
  Boolean(
    state.deposit?.paygroupCategories?.is_category_scroll_locked && state.deposit?.paygroupCategories?.is_in_carousel,
  );

export const depositMethodsSelector = (state: { deposit?: DepositSlice }) => state.deposit?.depositMethods || [];
export const shouldSkipInitFeatureSelector = (state: { deposit?: DepositSlice }) =>
  state.deposit?.shouldSkipInitFeature || false;

export const {
  setCurrentMethod,
  setDepositTax,
  cleanCurrentMethod,
  setIsAutoSubmitted,
  setSkippingAutoSelectMethod,
  resetSkippingAutoSelectMethod,
  setWidgetSettings,
  setUploadedFile,
  setIsDisplayPage,
  setPaygroupCategories,
  setDepositMethods,
  setShouldSkipInitFeature,
  setAmountStepConfig,
} = depositSlice.actions;
export default depositSlice.reducer;
